import React, { useEffect, useState } from "react";
import { Modal, Progress, Table, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { setActiveObj } from "../../features/obj/objSlice";
import axiosInstance from "../../instance";

// Projects behind schedule: time elapsed more than 10 points ahead of
// physical progress, or past their end date. The card shows the counts from
// getAllDashboardData (laggingSummary); "View more" opens the projects,
// paged by the server from getLaggingProjects.
//
// The table is fetched with its own loading state, not fetchData: the
// dashboard swaps to a full-page loader while fetchData is loading, which
// would blank the page on every page change.
const tableUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v2/dashboard/getLaggingProjects`;
const PAGE_SIZE = 10;

function LaggingProjectsTable() {
  const url = "api/v2/dashboard/getAllDashboardData";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = useSelector((state) => state.fetch.data[url] || []);
  const summary = data?.laggingSummary || {};

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  async function fetchTableData(page) {
    setLoading(true);
    try {
      const res = await axiosInstance.post(tableUrl, {
        start: page,
        limit: PAGE_SIZE,
      });
      if (res?.data?.success) {
        setRows(res?.data?.data?.result || []);
        setTotal(res?.data?.total || 0);
      } else {
        toast.error(res?.data?.message || "Could not load projects");
      }
    } catch (e) {
      toast.error("Could not load projects");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) fetchTableData(pageIndex);
  }, [open, pageIndex]);

  const handleOpen = () => {
    setPageIndex(0);
    setOpen(true);
  };

  const handleViewName = async (item) => {
    setOpen(false);
    await dispatch(setActiveObj({ key: "ongoingProject", data: item }));
    await navigate("/project-view");
  };

  const formatDate = (value) =>
    value ? moment(value).format("DD-MM-YYYY") : "--";

  // hint: shown under each title so the counts explain themselves.
  const stats = [
    {
      title: "Projects Checked",
      value: summary?.projectsChecked,
      hint: "Ongoing and stalled projects with a start and end date",
    },
    {
      title: "Gap Over 10%",
      value: summary?.gapOverTen,
      hint: "Time elapsed is more than 10% ahead of physical progress, even if the end date hasn't come yet",
      highlight: true,
    },
    {
      title: "Overdue",
      value: summary?.overdue,
      hint: "The end date has passed and the project isn't completed",
      highlight: true,
    },
    {
      title: "Behind Schedule",
      value: summary?.behindSchedule,
      hint: "Gap over 10% or overdue, each project counted once",
      highlight: true,
    },
  ];

  const elapsedText = (item) => {
    const months = item?.elapsedMonths;
    const days = item?.elapsedDays;
    return months != null && days != null
      ? `${months} month${months !== 1 ? "s" : ""} ${days} day${days !== 1 ? "s" : ""}`
      : "--";
  };

  // Amber when behind by up to 30 points, red beyond.
  const behindColor = (gap) => (Number(gap) > 30 ? "red" : "orange");

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      render: (_, __, index) => pageIndex * PAGE_SIZE + index + 1,
    },
    {
      title: "Project Name",
      width: "260px",
      render: (item) => (
        <div className="cursor-pointer" onClick={() => handleViewName(item)}>
          {item?.projectName}
        </div>
      ),
    },
    {
      title: "County",
      dataIndex: "countyName",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: (value) => formatDate(value),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: (value) => formatDate(value),
    },
    {
      // Work done against time used, one above the other, so the shortfall is
      // visible without reading numbers. The time bar fills at 100%; its label
      // carries the real figure, which passes 100% once the end date is gone.
      title: "Progress vs Time",
      key: "progressVsTime",
      width: "260px",
      render: (item) => {
        const done = Number(item?.physicalProgress) || 0;
        const used = Number(item?.timeElapsedPercent) || 0;
        const late = used > 100;
        return (
          <div className="flex flex-col gap-y-1 min-w-[14rem]">
            <div className="flex items-center gap-x-2">
              <span className="w-[4.5rem] text-[#6B7280] text-[.6875rem]">Work done</span>
              <Progress
                className="flex-1 !m-0"
                size="small"
                strokeColor="#4FAD5B"
                percent={Math.min(done, 100)}
                format={() => `${done}%`}
              />
            </div>
            <div className="flex items-center gap-x-2">
              <span className="w-[4.5rem] text-[#6B7280] text-[.6875rem]">Time used</span>
              <Progress
                className="flex-1 !m-0"
                size="small"
                strokeColor={late ? "#B81B0E" : "#0092CC"}
                percent={Math.min(used, 100)}
                format={() => (
                  <span style={{ color: late ? "#B81B0E" : undefined }}>{used}%</span>
                )}
              />
            </div>
            <span className="text-[#6B7280] text-[.6875rem]">{elapsedText(item)} elapsed</span>
          </div>
        );
      },
    },
    {
      title: "Behind By",
      dataIndex: "gap",
      render: (value) =>
        value != null ? (
          <Tag color={behindColor(value)}>{`Behind ${Number(value).toFixed(1)}%`}</Tag>
        ) : (
          "--"
        ),
    },
    {
      title: "Overdue",
      dataIndex: "overdue",
      render: (overdue, item) =>
        overdue ? (
          <Tag color="red">{`${item?.daysOverdue} day${item?.daysOverdue === 1 ? "" : "s"}`}</Tag>
        ) : (
          <Tag color="green">No</Tag>
        ),
    },
  ];

  return (
    <div className="p-[1.25rem] rounded-[.75rem] bg-white w-full">
      <div className="flex lg:flex-row flex-col lg:items-center justify-between gap-y-2">
        <div className="flex flex-col gap-y-1">
          <div className="text-[#050505] text-[.875rem] font-[400] font-roboto">
            Projects Behind Schedule
          </div>
          <span className="text-[#6B7280] text-[.75rem] font-[400] font-interSans">
            Time elapsed more than 10% ahead of physical progress, or past end date
          </span>
        </div>
        <button
          type="button"
          onClick={handleOpen}
          disabled={!summary?.behindSchedule}
          className={`border-none bg-transparent text-[.75rem] font-[500] font-interSans underline ${
            summary?.behindSchedule
              ? "text-[#0092CC] cursor-pointer"
              : "text-[#9CA3AF] cursor-not-allowed"
          }`}
        >
          View more
        </button>
      </div>

      <div className="mt-[1.5rem] flex flex-wrap items-stretch gap-[1.25rem] w-full">
        {stats.map((item, index) => (
          <React.Fragment key={item.title}>
            <div className="flex-1 min-w-[8rem]">
              <div
                className={`text-[.875rem] font-[400] font-roboto ${
                  item.highlight ? "text-[#B81B0E]" : "text-[#050505]"
                }`}
              >
                {item.title}
              </div>
              <div className="mt-[.25rem] min-h-[2rem] text-[#6B7280] text-[.6875rem] font-[400] font-interSans leading-[1.3]">
                {item.hint}
              </div>
              <div
                className={`mt-[.75rem] font-interSans text-[1.5rem] font-[500] ${
                  item.highlight ? "text-[#B81B0E]" : "text-[#050505]"
                }`}
              >
                {item.value ?? 0}
              </div>
            </div>
            {index !== stats.length - 1 && (
              <span className="lg:h-[4rem] h-0 lg:w-0 w-full lg:border-l border-b border-[#D6E1F5] lg:mx-[1rem]" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Projects the rule cannot judge, so nobody reads the counts as complete. */}
      {summary?.notMeasurable > 0 && (
        <div className="mt-[1rem] text-[#9CA3AF] font-interSans text-[.6875rem] font-[400]">
          {summary.notMeasurable} ongoing/stalled project
          {summary.notMeasurable === 1 ? "" : "s"} not checked: start or end date missing
        </div>
      )}

      <Modal
        title="Projects Behind Schedule"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <Table
          rowSelection={false}
          className="mt-[1rem] w-full"
          scroll={{
            x: "auto",
          }}
          rowKey={(record) => record?.projectId}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{
            position: ["bottomRight"],
            current: pageIndex + 1,
            total: total,
            pageSize: PAGE_SIZE,
            onChange: (page) => setPageIndex(page - 1),
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
        />
      </Modal>
    </div>
  );
}

export default LaggingProjectsTable;
